/**
 * Symptom cluster catalogue for the "how your symptoms connect" map.
 *
 * The 3 consumer buckets (sleep_energy / mood_mind / body) keep their editorial
 * copy, tools and colours, but their MEMBERSHIP is now derived from the shared
 * 100-symptom cluster map `SYMPTOM_TO_CLUSTER` (09_symptom_cluster_map.json — the
 * same source the provider report renders from). So every catalog symptom, incl.
 * the 40 new ones, lands in a consumer bucket and appears in the map; a NEW
 * catalog symptom flows in automatically via the shared map with no change here.
 *
 * WHICH clusters and chips actually render is still driven entirely by the user's
 * reported symptoms (see `buildClusters` in symptoms-vm.ts). Editorial copy
 * (cluster insight + per-symptom hormone education) lives in i18n under
 * `results.sym.clusters.*` and `results.sym.meta.*`.
 */
import { SYMPTOM_TO_CLUSTER } from '../../../doctor-report/report-view/symptom-clusters';

export interface ClusterDef {
  id: string;
  /** results.sym.clusters.<id>.label */
  labelKey: string;
  color: string;
  /** results.sym.clusters.<id>.title */
  titleKey: string;
  /** results.sym.clusters.<id>.body */
  bodyKey: string;
  /** tool ids surfaced as "tools that can help" */
  toolIds: string[];
  /** symptom ids that belong to this cluster (real symptomsForm ids) */
  symptomIds: string[];
}

type ConsumerBucket = 'sleep_energy' | 'mood_mind' | 'body';

/**
 * Rolls each of the 20 fine clinical clusters (keys of the shared
 * `SYMPTOM_TO_CLUSTER` map) up into one of the 3 broad consumer buckets. Sleep +
 * fatigue → sleep_energy; mood + motivation + focus/memory → mood_mind; every
 * physical group (thermal, pain, sensations, gut, heart, weight, skin, hair,
 * senses, urogenital, breasts…) → body. A cluster key not listed here defaults
 * to `body`, so a newly-added fine cluster still surfaces.
 */
const FINE_TO_CONSUMER: Record<string, ConsumerBucket> = {
  sleepFatigue: 'sleep_energy',
  energyFatigue: 'sleep_energy',
  moodEmotion: 'mood_mind',
  motivationConfidence: 'mood_mind',
  focusMemory: 'mood_mind',
  heatCold: 'body',
  painStiffness: 'body',
  skinNerveSensations: 'body',
  strengthMovement: 'body',
  digestionGut: 'body',
  heartBalance: 'body',
  appetiteWeight: 'body',
  skinCluster: 'body',
  hairScalpNails: 'body',
  sensitivities: 'body',
  headSenses: 'body',
  mouthTasteTeeth: 'body',
  vaginalIntimacy: 'body',
  urinaryPelvic: 'body',
  breasts: 'body',
};

/** catalog symptom ids that roll up into the given consumer bucket (from the shared map) */
const bucketSymptomIds = (bucket: ConsumerBucket): string[] =>
  Object.keys(SYMPTOM_TO_CLUSTER).filter(
    (id) => (FINE_TO_CONSUMER[SYMPTOM_TO_CLUSTER[id]] ?? 'body') === bucket,
  );

export const SYMPTOM_CLUSTERS: ClusterDef[] = [
  {
    id: 'sleep_energy',
    labelKey: 'results.sym.clusters.sleep_energy.label',
    color: '#1f8079',
    titleKey: 'results.sym.clusters.sleep_energy.title',
    bodyKey: 'results.sym.clusters.sleep_energy.body',
    toolIds: ['breathe', 'sleepex', 'water'],
    symptomIds: bucketSymptomIds('sleep_energy'),
  },
  {
    id: 'mood_mind',
    labelKey: 'results.sym.clusters.mood_mind.label',
    color: '#9b2e8e',
    titleKey: 'results.sym.clusters.mood_mind.title',
    bodyKey: 'results.sym.clusters.mood_mind.body',
    toolIds: ['mind', 'meditate', 'voice'],
    symptomIds: bucketSymptomIds('mood_mind'),
  },
  {
    id: 'body',
    labelKey: 'results.sym.clusters.body.label',
    color: '#b07a20',
    titleKey: 'results.sym.clusters.body.title',
    bodyKey: 'results.sym.clusters.body.body',
    toolIds: ['supps', 'tracker', 'water'],
    symptomIds: bucketSymptomIds('body'),
  },
];

/**
 * Ids that carry hormone-education detail (i18n
 * `results.sym.meta.<id>.{hormone,stage}`). These are the original 15 texts;
 * they name a specific hormone + physiological mechanism. The hormone chip was
 * removed from the report on 2026-06-02 (a condition for keeping the map), so
 * `hormone`/`stage` exist in i18n but are never rendered — kept hidden on
 * purpose. The other 85 popup texts (see SYMPTOM_META_IDS) deliberately carry
 * NO hormone attribution and have only a `body` string.
 */
export const HORMONE_META_IDS = new Set<string>([
  'insomnia_falling_asleep',
  'unrefreshing_sleep',
  'energy_dips',
  'motivation_drop',
  'night_sweats',
  'daytime_sleepiness',
  'anxiety',
  'brain_fog',
  'irritability',
  'difficulty_concentrating',
  'mood_swings',
  'joint_pain',
  'weight_gain',
  'dry_skin',
  'hair_thinning',
]);

/**
 * Symptom ids that have an educational popup body (i18n
 * `results.sym.meta.<id>.body`). Covers the full 102-item catalog: the original 15
 * hormone texts (HORMONE_META_IDS) + the 88 associative texts (85 merged
 * 2026-08-18, plus the three head ids split out of headaches_migraines on
 * 2026-09-10). 15 + 88 = 103, one more than the catalog because the retired
 * headaches_migraines id keeps its text so past scans still open a full card.
 * A symptom outside this set still renders in the map
 * with its real rating + co-occurring chips and falls back to the
 * catalog `description` for the body (see `descKey` in symptoms-vm.ts), so a tap
 * never opens an empty card — including any future catalog item.
 */
export const SYMPTOM_META_IDS = new Set<string>([
  // 15 hormone-education texts
  ...HORMONE_META_IDS,
  // 88 associative texts (10_cluster_popup_texts merged 2026-08-18, plus the three
  // head ids split out of headaches_migraines 2026-09-10)
  'abdominal_fat', 'abdominal_pain', 'adult_acne', 'allergy_increase', 'anger',
  'appetite_loss', 'back_pain', 'bloating', 'body_odor_change', 'breast_stinging',
  'breast_swelling', 'breast_tenderness', 'brittle_nails', 'burning_mouth',
  'chills_cold_spells', 'clumsiness', 'cold_hand_foot_sensitivity', 'constipation',
  'cramps_non_period', 'crawling_skin_sensation', 'deep_fatigue', 'diarrhea_gas',
  'dizziness_imbalance', 'dread', 'dry_eyes_gritty_feeling', 'dry_mouth',
  'early_awakening', 'electric_shock_sensations', 'foot_ankle_discomfort',
  'forgetfulness', 'frequent_cold_symptoms', 'general_weakness', 'grip_strength_loss',
  'gum_tenderness', 'hair_texture_change', 'hand_wrist_discomfort', 'headaches_migraines',
  'headaches_non_migraine',
  'heart_palpitations', 'heartburn_reflux', 'heat_intolerance', 'hot_feet', 'hot_flashes',
  'increased_facial_hair', 'insomnia_staying_asleep', 'itching_irritation_vulvar',
  'itchy_ears', 'itchy_sensitive_skin', 'light_sensitivity', 'low_mood', 'metallic_taste',
  'migraine_with_aura', 'migraine_without_aura', 'muscle_aches', 'nausea',
  'neck_shoulder_tension', 'noise_sensitivity',
  'pain_with_intercourse', 'pelvic_heaviness', 'phantom_smells', 'recurrent_utis',
  'reduced_libido', 'restless_legs', 'restlessness', 'salt_cravings', 'scalp_sensitivity',
  'self_confidence_drop', 'shoulder_restriction', 'skin_dark_patches', 'skin_rashes',
  'slow_recall', 'snoring', 'social_unease', 'social_withdrawal', 'stiffness',
  'stress_sensitivity', 'sudden_tears', 'sugar_cravings', 'taste_change',
  'tingling_or_numbness', 'tinnitus_ringing', 'tooth_sensitivity', 'urinary_frequency',
  'urinary_leakage', 'urinary_urgency', 'vaginal_discomfort', 'vaginal_dryness',
  'vision_fluctuation',
]);
